/**
 * The review bar's contract (issue #147): what the presentation needs, ready to draw. The adapter
 * (reviewBarAdapter.ts) builds it from the follow-up queue; the fixtures (ReviewBar.fixtures.ts)
 * by hand. The component knows nothing of the app: it reports which action was taken and draws the
 * outcome the page hands back.
 */
export type CaseCat = 'na' | 'foresporsler' | 'timer' | 'planlegg' | 'lonn';

export interface Fact {
  label: string;
  value: string;
}

/** What a case's button does. The page decides how, through its own guards and locks. */
export type CaseAction =
  /** answer a request the way the button says; the employee is told */
  | {
      kind: 'decide';
      what: 'offer' | 'swap' | 'absence';
      id: number;
      ok: boolean;
      label: string;
      consequence: string;
    }
  /** put in the actual clock-out time with a reason; correct only, or correct and approve */
  | { kind: 'correct-out'; shiftId: number; label: string }
  /** the page that owns the decision, opened through the page's unsaved-edit guard */
  | { kind: 'link'; to: string; label: string }
  /** the shift in the side panel on its clock times, where it is put right in place (owner 18.10) */
  | { kind: 'shift'; shiftId: number; label: string }
  /** a week item's own way in: its shift, its day or its week rule (the page resolves `item`) */
  | { kind: 'open'; item: string; label: string }
  /** Finn erstatter (F1): the page's candidate panel for this shift; the bar waits for its answer */
  | { kind: 'cover'; shiftId: number; date: string | null; label: string }
  /** approve these shifts as clocked, one PUT /admin/shifts/:id/accept each (timeliste/approve.tsx) */
  | { kind: 'approve'; shiftIds: number[]; label: string }
  /** put in the clock-in a shift is missing, from the field the case shows (PUT /admin/shifts/:id) */
  | { kind: 'correct-in'; shiftId: number; label: string }
  /** Ring or Send melding: a tel: or sms: link, only when the person has a phone */
  | { kind: 'contact'; href: string; label: string }
  /** the person's card, where what is missing on them is filled in */
  | { kind: 'person'; id: number; label: string }
  /** the actual break put right in Saker: start and length, with a reason (S07, #286) */
  | { kind: 'pause'; shiftId: number; label: string }
  /** the later of two shifts moved to give the rest the rule asks for, saved in the plan (S11, #286) */
  | { kind: 'plan'; shiftId: number; label: string }
  /** Kontroll with exactly the worklist's records and Saker as the origin (S09, #286) */
  | { kind: 'kontroll'; label: string };

/** The correction field's starting point: what is recorded, so the input can be checked against it. */
export interface CorrectOut {
  shiftId: number;
  /** ISO instant the person clocked in; null when the clock-in is what is missing */
  punchIn: string | null;
  /** ISO instants the shift was planned to start and end; the field starts from one of them */
  plannedStart: string;
  plannedEnd: string;
  /** "8. okt." for the field label */
  dayLabel: string;
}

/** A shift as the case's who-and-when draws it: its time and the small round dot of its type's colour. */
export interface CaseShift {
  /** "14:30–22:30" */
  time: string;
  type: string;
  color: string | null;
  /** "Lør 31. okt.", where the figure names its day */
  day?: string | undefined;
}

/**
 * The facts of a kind as a figure, only where a drawing adds understanding (owner's review
 * 2026-10-28). The adapter computes every number from the case's data; the component only draws.
 * `big` and `legend` are the one figure in words above the drawing, when the kind has one.
 */
export type CaseFigure = {
  big?: string | undefined;
  legend?: string | undefined;
} & (
  | {
      /** the planned gap between two shifts against the hours the law asks for */
      kind: 'rest';
      one: CaseShift & { start: string; end: string };
      two: CaseShift & { start: string; end: string };
      need: number;
    }
  /** one number against its limit: grey up to the limit, the accent from it */
  | { kind: 'meter'; value: number; limit: number; marks: { at: number; label: string }[] }
  /** per hour of the need: the people planned and the people short */
  | { kind: 'need'; cols: { h: number; have: number; need: number }[] }
  /** a shift against the night window of the person's age group (AML § 11-3), hours past the start day's midnight */
  | {
      kind: 'youth';
      shift: CaseShift;
      from: number;
      to: number;
      nightFrom: number;
      eveningFrom?: number | undefined;
    }
);

export interface ReviewCase {
  /** stable across reads: the same request, shift or worklist keeps its key */
  key: string;
  /** the keys of every condition this one decision settles, when it groups several (S02, #286) */
  keys?: string[] | undefined;
  cat: CaseCat;
  /** 'd' a breach or something that blocks pay, 'w' needs a look, 'i' routine */
  tone: 'd' | 'w' | 'i';
  /** context line: the person, the date, and whether it is outside the period on screen */
  person?: { id: number; name: string } | undefined;
  date?: string | undefined;
  dateLabel?: string | undefined;
  /** "Nå", "Tidligere", "Neste 7 dager" */
  when: string;
  /** the short name a list row and a toast use */
  title: string;
  description: string;
  /** the template's parts (prototype case-templates, owner 2026-10-28); each falls back when absent */
  /** what, in one sentence built from the data: "Daysi har ikke stemplet inn" (else the title) */
  what?: string | undefined;
  /** the date in full: "I dag, onsdag 28. oktober", "2. til 4. november" (else dateLabel) */
  dateText?: string | undefined;
  /** the shift the case is about, with its type's dot */
  shift?: CaseShift | undefined;
  fig?: CaseFigure | undefined;
  /** why it matters in one line (else the description), and the law it rests on or «Lønn» */
  why?: string | undefined;
  law?: string | undefined;
  /** the word beside the dot when the case is a state, not a severity: «Venter på svar» */
  status?: string | undefined;
  /** what answering does, one line each, in place of a figure */
  outcome?: string[] | undefined;
  /** days that go back on a vacation balance: the balance line is drawn only when the client can read it */
  balance?:
    | { userId: number; field: 'vacation_days_balance' | 'next_year_vacation_days_balance'; back: number }
    | undefined;
  facts: Fact[];
  /** the recommended action first */
  actions: CaseAction[];
  correct?: CorrectOut | undefined;
  /** the shift the plan marks while the case is open */
  shiftId?: number | undefined;
  /** that shift's planned start and end (ISO): «Haster nå» is a shift running or starting within 24 hours */
  span?: { start: string; end: string } | undefined;
  /** a worklist holds this many records; absent for a single case */
  records?: number | undefined;
  /**
   * A worklist's records, each with its person and day (#286): the period and team decide which belong here
   * (saker.ts scoped), and Kontroll receives exactly these ids.
   */
  members?:
    | {
        id: number;
        userId: number | null;
        date: string;
        label?: string | undefined;
        hours?: number | undefined;
      }[]
    | undefined;
  /** the records of this worklist in the period that are in other teams (saker.ts scoped) */
  elsewhere?: number | undefined;
  /** the records that may be approved together as they are; absent when one needs a look first */
  approve?: number[] | undefined;
  /** information only, no decision: listed apart and never counted (S01, #286) */
  info?: boolean | undefined;
  /** the conditions one decision settles, each in words, when there is more than one (S02, #286) */
  findings?: string[] | undefined;
  /** when the task is done, said before it is acted on (#286 «Når er oppgaven ferdig?») */
  finish?: string | undefined;
  /** the person has no phone a call can be placed to: say so, never a dead call link (S06) */
  noPhone?: boolean | undefined;
  /** the clocked shift a break correction works on (S07) */
  pause?:
    | { shiftId: number; punchIn: string; punchOut: string; lunchIn: string | null; lunchOut: string | null }
    | undefined;
  /** a rest task's two shifts and the configured rule in hours (S11) */
  rest?:
    | {
        before: { id: number; start: string; end: string };
        after: { id: number; start: string; end: string; published: boolean };
        need: number;
      }
    | undefined;
  /** a pending absence request: the page reads what approving it deducts, and sends that amount */
  absence?:
    | {
        id: number;
        typeId: number;
        type: string;
        /** 'date' whole days, else the absence's own clock times */
        kind: 'date' | 'time' | 'datetime';
        start: string;
        end: string;
        deductible: boolean;
        typeActive: boolean;
      }
    | undefined;
}

/** What happened to an action, as the server said it. */
export type CaseOutcome =
  | {
      kind: 'saved';
      /** "Utstempling 15:30 lagt inn og timene godkjent" */
      text: string;
      /** "Ola Nordmann er varslet", or what is not yet decided */
      detail?: string | undefined;
      /** a real inverse of this one action, offered until something makes it final */
      undo?: (() => Promise<CaseOutcome>) | undefined;
    }
  /** part saved, part refused: said apart, never as one success */
  | { kind: 'partial'; text: string; message: string }
  /** the server said no, with its reason; nothing was saved; a conflict names what moved and holds now (S18) */
  | { kind: 'refused'; message: string; changed?: { label: string; value: string }[] | undefined }
  /** no answer: it may or may not be saved; the page reads again before anything else */
  | { kind: 'unknown' }
  /** the manager left without choosing (the candidate panel closed): nothing was sent */
  | { kind: 'cancelled' };

/**
 * What an action carries from its form (#286): a clock time with its reason (correct-in, correct-out), a break
 * (pause), a moved shift (plan), the records chosen (approve) or a decision's reason (decide).
 */
export interface ActInput {
  at?: string | undefined;
  reason?: string | undefined;
  approve?: boolean | undefined;
  lunchIn?: string | null | undefined;
  lunchOut?: string | null | undefined;
  start?: string | undefined;
  end?: string | undefined;
  ids?: number[] | undefined;
}

/** A read that has not answered or failed, with the categories it feeds: those are not known clear. */
export interface SourceGap {
  label: string;
  cats: CaseCat[];
  state: 'loading' | 'failed';
}

export const CATS: [CaseCat, string][] = [
  ['na', 'Nå'],
  ['foresporsler', 'Forespørsler'],
  ['timer', 'Godkjenn timer'],
  ['planlegg', 'Planlegg'],
  ['lonn', 'Lønn'],
];

/** The fixed reasons a clock time is corrected for (UC7, #286 S05); the server needs three letters at least. */
export const CORRECTION_REASONS = [
  'Avklart med den ansatte',
  'Bekreftet av ansvarlig på jobb',
  'Glemte å stemple ut',
  'Glemte å stemple inn',
  'Stemplingen virket ikke',
  'Avtalt med leder',
  'Jobbet lenger enn planlagt',
  'Gikk tidligere enn planlagt',
];
